import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import DropdownIcon from 'assets/icons/arrow-down-grey.svg?react';
import Eye from 'assets/icons/read-only-eye.svg?react';
import HasChangesIcon from 'assets/icons/has-changes.svg?react';

import styles from './DocumentListItem.module.scss';
import { Links } from 'application/constants/links';
import { AppMenuItem } from 'components/uikit/menu/AppMenu';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { TextButton } from 'components/uikit/buttons/TextButton.tsx';
import { BrightTooltip } from 'components/tooltips/BrightTooltip.tsx';
import { DefaultTooltip } from 'components/tooltips/DefaultTooltip.tsx';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import {
  DocumentDeletionBlockReason,
  DocumentExportFormat,
  ExternalEditingType,
  OperationType,
  ProjectResourceType,
  Status,
  SystemVariableType,
} from 'services/api/api-client';
import { QueryFactory } from 'services/api';
import {
  downloadDocument,
  downloadReadOnlyDocument,
} from 'helpers/documents/fileHandler.ts';
import { openDocumentExternally } from 'helpers/document-external-opening.ts';
import { useGridTemplate } from 'helpers/useGridTemplate.ts';
import useElementTextOverflow, {
  OverflowDirection,
} from 'helpers/useElementTextOverflow.ts';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext.tsx';
import { useVariableLockSync } from 'pages/authorized/variables/useVariableLockSync.ts';
import { getVariableLockInfo } from 'pages/authorized/variables/components/variableCard/useVariableCardAppMenuItems.tsx';
import { RefreshVariableModal } from 'pages/authorized/variables/components/refreshVariableModal/RefreshVariableModal.tsx';

import { StatusDropdown } from '../statuses/statusDropdown/StatusDropdown.tsx';
import { SignatureBlock } from '../signatureBlock/SignatureBlock.tsx';
import { CancelDocumentSigningModal } from '../cancelDocumentSigningModal/CancelDocumentSigningModal.tsx';
import { ResendNotificationModal } from '../resendNotificationModal/ResendNotificationModal.tsx';
import { HasChangesTooltipTitle } from '../tooltips/HasChangesTooltipTitle.tsx';
import { useDocumentContext } from '../../documentContext/DocumentContext.tsx';
import { useDocumentLockStatus } from '../../components/documentLock/useDocumentLockStatus.ts';

import { TreeItemShell } from './shared/TreeItemShell.tsx';
import { TreeItemHandle } from './shared/TreeItemHandle.tsx';
import { TreeItemName } from './shared/TreeItemName.tsx';
import { columnClass, TreeItemColumn } from './shared/TreeItemColumn.tsx';
import { TreeItemStatusColumn } from './shared/TreeItemStatusColumn.tsx';
import { TreeItemDotMenu } from './shared/TreeItemDotMenu.tsx';
import { TreeItemDeleteModal } from './shared/TreeItemDeleteModal.tsx';
import { TreeItemEditModal } from './shared/TreeItemEditModal.tsx';
import { useContextualMenu } from './shared/useContextualMenu.ts';
import { useTreeItemMenuItems } from './shared/useTreeItemMenuItems.ts';
import { DocumentRowProps } from './shared/types.ts';

type VariableUpdateModal = {
  isOpen: boolean;
  variableId?: number;
  variableName?: string;
};

export const DocumentListItem = forwardRef<HTMLDivElement, DocumentRowProps>(
  (props, ref) => {
    const { item, parent } = props;

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isStopSigningModalOpen, setIsStopSigningModalOpen] = useState(false);
    const [isResendNotificationModalOpen, setIsResendNotificationModalOpen] =
      useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [variableUpdateModalOpen, setVariableUpdateModalOpen] =
      useState<VariableUpdateModal>({ isOpen: false });
    const [selectedStatus, setSelectedStatus] = useState(item.status);

    const i18n = useScopedTranslation(
      'Page.Projects.ProjectDetailsPage.FoldersTree.TreeItem',
    );
    const i18n_Modal = useScopedTranslation('Component.CommonModals');
    const i18n_UpdateVariableModal = useScopedTranslation(
      'Component.UpdateVariableModal',
    );
    const deleteModalTitle = useScopedTranslation('Component.DeleteItemModal');

    const queryClient = useQueryClient();
    const confirmModal = useModal();
    const contextualMenu = useContextualMenu();
    const { createDeleteMenuItem, createEditMenuItem } = useTreeItemMenuItems();
    const { setDocumentState } = useDocumentContext();
    const { refreshVariables, variablesQuery } = useVariablesContext();

    const statusAnchorRef = useRef<HTMLElement>(null);
    const [showStatusMenu, setShowStatusMenu] = useState(false);

    const hasChildren = item.children && item.children.length > 0;
    const isReadOnly = item.isReadOnly;

    const documentNameVariable = variablesQuery.data?.data.find(
      (x) =>
        x.systemVariableType === SystemVariableType.Name &&
        x.basedOnDocument?.id === item.documentId,
    );

    useVariableLockSync(
      documentNameVariable ? [documentNameVariable.id] : [],
      item.revisionId,
      refreshVariables,
    );

    const hasPermission = useHasProjectPermission();
    const hasAllDocumentPermissions = hasPermission([
      permission(
        ProjectResourceType.Document,
        OperationType.Create,
        OperationType.Read,
        OperationType.Update,
        OperationType.Delete,
      ),
    ]);
    const hasUpdateDocumentPermissions = hasPermission([
      permission(ProjectResourceType.Document, OperationType.Update),
    ]);
    const hasDeleteDocumentPermissions = hasPermission([
      permission(ProjectResourceType.Document, OperationType.Delete),
    ]);

    const { lockQuery, isLockedByAnotherSession } = useDocumentLockStatus(
      item.documentId,
      hasUpdateDocumentPermissions || hasDeleteDocumentPermissions,
    );

    const isEditedByAnotherSession = lockQuery.data
      ? isLockedByAnotherSession
      : item.deletionBlockReason ===
        DocumentDeletionBlockReason.HasActiveEditors;

    const deletionBlockReason =
      item.deletionBlockReason ===
      DocumentDeletionBlockReason.HasDerivedDocuments
        ? DocumentDeletionBlockReason.HasDerivedDocuments
        : isEditedByAnotherSession
          ? DocumentDeletionBlockReason.HasActiveEditors
          : DocumentDeletionBlockReason.None;

    const lockReason = useMemo(() => {
      if (isEditedByAnotherSession) return i18n.t('document_edit_is_locked');

      const variableLockReason = getVariableLockInfo(
        documentNameVariable?.lockStatuses,
      ).reason;
      if (variableLockReason) return i18n.t('document_edit_is_locked_variable');

      return null;
    }, [documentNameVariable?.lockStatuses, isEditedByAnotherSession]);

    const updateSigningStatus = async () => {
      await QueryFactory.DocumentSigningClient.updateRevisionDocumentsSigningStatus(
        item.revisionId,
      );
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.DocumentQuery.searchQueryKey(item.revisionId),
      });
      await queryClient.invalidateQueries({
        queryKey:
          QueryFactory.DocumentSigningQuery.getDocumentsSigningStatusQueryKey(
            item.revisionId,
          ),
      });
    };

    const openDocumentExternallyFromItem = async (
      type: ExternalEditingType,
    ) => {
      if (
        item.isExported &&
        !(await confirmModal.showConfirm({
          title: i18n_Modal.t('external_edit'),
          text: i18n_Modal.t('external_edit_message'),
          okButtonText: i18n_Modal.t('external_edit_ok_button'),
        }))
      ) {
        return;
      }

      setIsLoading(true);
      try {
        await openDocumentExternally(item.documentId, type);
      } catch (e) {
        showErrorToast(e);
      } finally {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.RevisionQuery.getQueryKey(item.revisionId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DocumentQuery.searchQueryKey(item.revisionId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.ProjectQuery.getQueryKey(item.projectId),
        });
        setIsLoading(false);
      }
    };

    const onItemDelete = async () => {
      try {
        await item.onDelete?.(item.id);
      } catch (e) {
        showErrorToast(e);
        return;
      }
    };

    const onDocumentEdit = async (
      value: string,
      editedProperty: 'status' | 'name',
    ) => {
      if (editedProperty === 'name') {
        try {
          await QueryFactory.DocumentClient.patch(item.documentId, {
            name: value,
          });
        } catch (e) {
          showErrorToast(e);
          return;
        }
      }

      await item.onDocumentEdit?.(item.id, value, editedProperty);
    };

    const downloadAs = async (exportFormat: DocumentExportFormat) => {
      contextualMenu.hide();

      setIsLoading(true);
      await downloadDocument(item.documentId, item.revisionId, exportFormat);
      setIsLoading(false);
    };

    const openIn = async (type: ExternalEditingType) => {
      try {
        contextualMenu.hide();
        await openDocumentExternallyFromItem(type);
      } catch (e) {
        showErrorToast(e);
      }
    };

    const menuItems = useMemo(() => {
      const items: AppMenuItem[] = [
        {
          key: 'open-in',
          text: i18n.t('open-in'),
          className: styles.paleMenuItem,
          disabled: true,
          onClick: () => {},
          hidden: !hasUpdateDocumentPermissions,
        },
        {
          key: 'office-365-browser',
          text: i18n.t('office-365-browser'),
          className: styles.menuItem,
          onClick: async () =>
            await openIn(ExternalEditingType.SharepointWordBrowser),
          hidden: !hasUpdateDocumentPermissions,
        },
        {
          key: 'office-365-word',
          text: i18n.t('office-365-word'),
          className: styles.separatorMenuItem,
          onClick: async () =>
            await openIn(ExternalEditingType.SharepointWordApp),
          hidden: !hasUpdateDocumentPermissions,
        },
        {
          key: 'download',
          text: i18n.t('download'),
          className: styles.paleMenuItem,
          disabled: true,
          onClick: () => {},
        },
        {
          key: 'as-docx',
          text: i18n.t('as-docx'),
          className: styles.menuItem,
          onClick: async () => await downloadAs(DocumentExportFormat.OpenXml),
        },
        {
          key: 'as-pdf',
          text: i18n.t('as-pdf'),
          className: styles.separatorMenuItem,
          onClick: async () => await downloadAs(DocumentExportFormat.Pdf),
        },
        createEditMenuItem({
          lockReason,
          hidden: !hasUpdateDocumentPermissions,
          onClick: () => {
            contextualMenu.hide();
            setIsEditModalOpen(true);
          },
        }),
      ];

      if (item.status === Status.Signing) {
        items.push(
          {
            key: 'sign',
            text: i18n.t('check_signatures'),
            className: styles.menuItem,
            onClick: async () => {
              try {
                contextualMenu.hide();
                setIsLoading(true);
                await updateSigningStatus();
              } catch (e) {
                showErrorToast(e);
              } finally {
                setIsLoading(false);
              }
            },
            hidden: !hasUpdateDocumentPermissions,
          },
          {
            key: 'resend-notification',
            text: i18n.t('resend_notification_button'),
            className: styles.menuItem,
            disabled: hasChildren,
            onClick: async () => setIsResendNotificationModalOpen(true),
            hidden: !hasUpdateDocumentPermissions,
          },
          {
            key: 'cancel-signing',
            text: i18n.t('cancel_signing_button'),
            className: styles.delete,
            disabled: hasChildren,
            tooltipMessage: i18n.t('cancel_signing_message'),
            onClick: async () => setIsStopSigningModalOpen(true),
            hidden: !hasUpdateDocumentPermissions,
          },
        );
      }

      items.push(
        createDeleteMenuItem({
          disabled: deletionBlockReason !== DocumentDeletionBlockReason.None,
          deletionBlockReason,
          hidden: !hasDeleteDocumentPermissions,
          onClick: () => {
            contextualMenu.hide();
            setIsDeleteModalOpen(true);
          },
        }),
      );

      return items;
    }, [
      i18n,
      item.id,
      item.status,
      item.documentId,
      item.revisionId,
      item.projectId,
      item.isExported,
      deletionBlockReason,
      hasChildren,
      lockReason,
      hasUpdateDocumentPermissions,
      hasDeleteDocumentPermissions,
    ]);

    useEffect(() => {
      setSelectedStatus(item.status);
    }, [item.projectId, item.revisionId, item.status]);

    const { grid, hiddenColumns } = useGridTemplate(
      item.projectId,
      'DocumentListItem',
    );

    const [dateRef, isDateOverflowing] = useElementTextOverflow<HTMLDivElement>(
      OverflowDirection.Horizontal,
    );

    const itemName = item.isOpenXml ? (
      <AppLink
        testId="list-item-name"
        to={Links.Authorized.OpenDocument.link({
          documentId: item.documentId,
          projectId: item.projectId,
          releaseId: item.revisionId,
        })}
        className={styles.link}
      >
        <div
          onClick={() => {
            setDocumentState((prevState) => ({
              ...prevState,
              enableDocumentUpdate: true,
            }));
          }}
        >
          {item.name}
        </div>
      </AppLink>
    ) : (
      <a
        className={styles.link}
        onClick={async () => {
          await downloadReadOnlyDocument(item.documentId);
        }}
      >
        {item.name}
      </a>
    );

    return (
      <TreeItemShell
        ref={ref}
        treeProps={props}
        isLoading={isLoading}
        modals={
          <>
            <CancelDocumentSigningModal
              projectId={item.projectId}
              revisionId={item.revisionId}
              documentId={item.documentId}
              isOpen={isStopSigningModalOpen}
              onClose={() => {
                setIsStopSigningModalOpen(false);
                contextualMenu.hide();
              }}
            />
            <ResendNotificationModal
              revisionId={item.revisionId}
              documentId={item.documentId}
              isOpen={isResendNotificationModalOpen}
              onClose={() => {
                setIsResendNotificationModalOpen(false);
                contextualMenu.hide();
              }}
            />
            <TreeItemDeleteModal
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              title={deleteModalTitle.t('title_document')}
              item={item}
              projectId={item.projectId}
              revisionId={item.revisionId}
              onDelete={onItemDelete}
            />
            <TreeItemEditModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              item={item}
              parent={parent}
              onSubmit={async (name) => {
                await onDocumentEdit(name, 'name');
                await refreshVariables();
              }}
            />
            <CustomModal
              isOpen={variableUpdateModalOpen.isOpen}
              title={i18n_UpdateVariableModal.t('title')}
              onClose={() => setVariableUpdateModalOpen({ isOpen: false })}
              isBlocking={true}
            >
              <RefreshVariableModal
                variableId={variableUpdateModalOpen.variableId!}
                variableName={variableUpdateModalOpen.variableName!}
                projectId={item.projectId}
                revisionId={item.revisionId}
                onVariableUpdate={async () => {
                  await QueryFactory.RevisionClient.updateVariables(
                    item.revisionId,
                    [variableUpdateModalOpen.variableId!],
                  );
                }}
                onClose={() => setVariableUpdateModalOpen({ isOpen: false })}
              />
            </CustomModal>
          </>
        }
      >
        <TreeItemHandle
          itemId={item.id}
          disableSorting={item.disableSorting}
          handleProps={props.handleProps}
          expanded={false}
        >
          {!isReadOnly && <DropdownIcon className={styles.folderArrowHidden} />}
          {isReadOnly && (
            <DefaultTooltip title={i18n.t('read_only')} placement={'top'}>
              <div className={styles.readOnlyEye}>
                <Eye />
              </div>
            </DefaultTooltip>
          )}

          {(item.expiredVariables?.length > 0 || item.templateWithUpdate) && (
            <div className={styles.hasChanges}>
              <BrightTooltip
                placement="top"
                title={
                  <HasChangesTooltipTitle
                    item={item}
                    onRefreshButtonClick={(e) => {
                      setVariableUpdateModalOpen({
                        isOpen: true,
                        variableId: e.variableId,
                        variableName: e.variableName,
                      });
                    }}
                  />
                }
              >
                <TextButton
                  className={styles.hasChangesTooltip}
                  icon={<HasChangesIcon />}
                />
              </BrightTooltip>
            </div>
          )}
        </TreeItemHandle>

        <div className={styles.wrapper}>
          <div
            className={styles.treeItem}
            style={{ gridTemplateColumns: grid }}
          >
            <TreeItemName
              name={item.name}
              auditData={item.auditData}
              showTooltip={hasAllDocumentPermissions}
            >
              {itemName}
            </TreeItemName>

            <TreeItemStatusColumn
              hiddenColumns={hiddenColumns}
              itemType={item.type}
              status={selectedStatus}
              disabled={!hasUpdateDocumentPermissions}
              statusAnchorRef={statusAnchorRef}
              onClick={() => {
                if (
                  [
                    Status.Draft,
                    Status.ReadyForReview,
                    Status.CanBeSigned,
                    Status.NotApplicable,
                    Status.Signed,
                  ].includes(selectedStatus)
                ) {
                  setShowStatusMenu(true);
                }
              }}
            />

            {item.versionUpdaterUser != null ? (
              <BrightTooltip
                className={columnClass(hiddenColumns, 'version')}
                title={
                  <div className={styles.tooltipMessage}>
                    <strong>{i18n.t('version_update_tooltip_title')}</strong>
                    <div className={styles.tooltipContentWrapper}>
                      <div className={styles.tooltipContentName}>
                        {item.versionUpdaterUser.name}
                      </div>
                      <div className={styles.tooltipContentDate}>
                        {format(item.versionUpdaterUser.updatedAt, 'PPp')}
                      </div>
                    </div>
                  </div>
                }
                placement={'top'}
                disableHoverListener={!hasAllDocumentPermissions}
              >
                <div data-test-id="version" className={styles.treeItemVersion}>
                  <div className={styles.treeItemVersionUpdatedIndicator}></div>
                  {item.version}
                </div>
              </BrightTooltip>
            ) : (
              <TreeItemColumn
                column="version"
                hiddenColumns={hiddenColumns}
                className={styles.treeItemVersion}
                testId="version"
              >
                {item.version}
              </TreeItemColumn>
            )}

            <BrightTooltip
              title={<div className={styles.toolTip}>{item.date}</div>}
              disableHoverListener={!isDateOverflowing}
              placement="top"
            >
              <div
                data-test-id="date"
                className={columnClass(
                  hiddenColumns,
                  'date',
                  styles.treeItemDate,
                )}
                ref={dateRef}
              >
                {item.date}
              </div>
            </BrightTooltip>

            <TreeItemColumn column="sign" hiddenColumns={hiddenColumns}>
              <SignatureBlock
                projectId={item.projectId}
                revisionId={item.revisionId}
                documentId={item.documentId}
              />
            </TreeItemColumn>
          </div>

          <TreeItemDotMenu
            permissions={[]}
            menuItems={menuItems}
            anchorRef={contextualMenu.anchorRef}
            isOpen={contextualMenu.isOpen}
            onShow={contextualMenu.show}
            onClose={contextualMenu.hide}
          />

          <StatusDropdown
            statusAnchorRef={statusAnchorRef.current}
            isOpen={showStatusMenu}
            onClose={async (statusSelection) => {
              if (!statusSelection) {
                setShowStatusMenu(false);
                return;
              }
              const previousStatus = selectedStatus;

              try {
                await QueryFactory.DocumentClient.patch(item.documentId, {
                  status: statusSelection,
                });
                await setStatus(statusSelection);
              } catch (e) {
                showErrorToast(e);
                await setStatus(previousStatus);
              }

              async function setStatus(statusSelection: Status) {
                if (!statusSelection) {
                  return;
                }
                setSelectedStatus(statusSelection);
                await onDocumentEdit(statusSelection, 'status');
                setShowStatusMenu(false);
              }
            }}
          />
        </div>
      </TreeItemShell>
    );
  },
);
